import React from "react";
import dayjs from "dayjs";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { LandTourSurcharge } from "../../../../interfaces/land-tour.interface";
import GroundTourModal from "./GroundTourModal";
import { PAX_TYPES } from "./ChooseYourTour";

interface Props {
  open: boolean;
  onClose: () => void;
  surcharges: LandTourSurcharge[];
  // The tour category the surcharges apply to, shown in the title.
  categoryName?: string;
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatAmount = (price: { price_type: string; amount_price: number; percentage?: number }) =>
  price.price_type === "percentage" ? `${price.percentage}%` : `RM ${Number(price.amount_price).toFixed(2)}`;

const categoryLabel = (category: string) =>
  PAX_TYPES.find(({ key }) => key === category)?.label ||
  (category === "normal" ? "Each" : category.replace(/_/g, " "));

// The weekday list gets its own line under the date range, rather than trailing it in
// parentheses, so a full week ("Sun, Mon, ... Sat") doesn't wrap mid-list.
const periodLabel = (surcharge: LandTourSurcharge): { range: string; weekdays: string | null } | null => {
  if (surcharge.dates?.length) {
    return { range: surcharge.dates.map((date) => dayjs(date).format("DD MMM YYYY")).join(", "), weekdays: null };
  }

  if (surcharge.period?.start_date && surcharge.period?.end_date) {
    // "start to end" matches B2B's own surcharge table (land_tour/priceDetails.js).
    const range = `${dayjs(surcharge.period.start_date).format("DD MMM YYYY")} to ${dayjs(surcharge.period.end_date).format("DD MMM YYYY")}`;
    const weekdays = surcharge.period.weekdays?.length
      ? surcharge.period.weekdays.map((day) => WEEKDAY_LABELS[Number(day)]).join(", ")
      : null;
    return { range, weekdays };
  }

  return null;
};

export default function SurchargesModal({ open, onClose, surcharges, categoryName }: Props) {
  const isMobile = useBreakpoint();
  if (!open) return null;

  return (
    <GroundTourModal
      open={open}
      onClose={onClose}
      title={categoryName ? `Surcharge Price for ${categoryName}` : "Surcharges"}
      isMobile={isMobile}
      width={975}
      className={`ground-tour-show-v2__surcharge-popup ${isMobile ? "" : "ground-tour-show-v2__surcharge-popup--desktop"}`}
    >
      <div className="ground-tour-show-v2__surcharge-table-wrap">
        <table className="ground-tour-show-v2__surcharge-table">
          <colgroup>
            <col />
            <col />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Period</th>
              <th>Prices</th>
            </tr>
          </thead>
          <tbody>
            {surcharges.map((surcharge) => (
              <tr key={surcharge.id}>
                <td>{surcharge.title}</td>
                <td>{surcharge.surcharge_type_title}</td>
                <td>
                  {(() => {
                    const period = periodLabel(surcharge);
                    if (!period) return "-";
                    return (
                      <>
                        <div>{period.range}</div>
                        {period.weekdays && <div>({period.weekdays})</div>}
                      </>
                    );
                  })()}
                </td>
                <td>
                  {surcharge.prices.map((price, index) => (
                    <div key={index}>
                      <strong>{categoryLabel(price.category)}:</strong> {formatAmount(price)}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GroundTourModal>
  );
}
